import type { CATEGORY_TYPE } from '@/types/categoryConst';
import type { FeedbackListItemWithBlock } from '@/types/feedback';
import { FEEDBACK_COLUMNS, SOURCE_COLUMNS } from '@/types/filterKeyConst';
import { buildObjQuery } from '@/utils/util';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { defaultAxios } from '..';

const TEXT_FILTER_KEYS_REQUIRING_PREFIX_TRIM = new Set([
  FEEDBACK_COLUMNS.HASHTAG,
  FEEDBACK_COLUMNS.MENTION,
  FEEDBACK_COLUMNS.KEYWORD,
]);

const sanitizeFilterTextValue = (value: StringNumberValue): StringNumberValue => {
  if (typeof value !== 'string') return value;
  return value.replace(/^[@#]+/, '').trim();
};

const sanitizeStructuredFilterPayload = (payload: StructuredFilter) => {
  const nextPayload: StructuredFilter = { ...payload };

  for (const filterKey of TEXT_FILTER_KEYS_REQUIRING_PREFIX_TRIM) {
    const valueSets = payload[filterKey];
    if (!Array.isArray(valueSets)) continue;

    nextPayload[filterKey] = valueSets.map((valueSet) => {
      const curr = valueSet as StructuredFilterStringNumberValueSet;
      if (!Array.isArray(curr?.values)) return valueSet;
      return {
        ...curr,
        values: curr.values.map(sanitizeFilterTextValue),
      };
    });
  }

  return nextPayload;
};

export const buildWithBlockDatePayload = (payload: StructuredFilter) => {
  return payload;
};

const feedbackService = {
  async fetchFeedbackList({
    query = {
      sortby: 'date',
      order: 'desc',
      cursor: undefined,
      direction: 'next',
    },
    payload,
    isPublic,
    workspaceAlias,
  }: {
    query?: {
      sortby?: string;
      order?: string;
      cursor?: string;
      direction?: string;
    };
    payload: StructuredFilter;
    isPublic?: boolean;
    workspaceAlias?: string;
  }): Promise<FeedbackListT> {
    try {
      const {
        cursor,
        sortby = 'date',
        order = 'desc',
        direction = 'next',
      } = query;
      const wa = workspaceAlias ?? getCurrWorkspaceAlias();
      const sanitizedPayload = sanitizeStructuredFilterPayload(payload);
      const url = isPublic
        ? `/workspaces/${wa}/feedbacks/public`
        : `/workspaces/${wa}/feedbacks`;
      const res = await defaultAxios.post<FeedbackListT>(
        url,
        sanitizedPayload,
        {
          params: {
            cursor,
            sortby,
            order,
            direction,
          },
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async fetchFeedbackBlockList({
    query = {
      sortby: 'date',
      order: 'desc',
      cursor: undefined,
      direction: 'next',
    },
    payload,
  }: {
    query?: {
      sortby: string;
      order: OrderStatusT;
      cursor?: string;
      direction?: string;
    };
    payload: StructuredFilter;
  }): Promise<FeedbackBlockListT> {
    try {
      const { cursor, sortby, order, direction = 'next' } = query;
      const wa = getCurrWorkspaceAlias();
      const sanitizedPayload = sanitizeStructuredFilterPayload(payload);
      const withBlockPayload = buildWithBlockDatePayload(sanitizedPayload);
      const res = await defaultAxios.post(
        `/workspaces/${wa}/feedbacks/with_block`,
        withBlockPayload,
        {
          params: {
            cursor,
            sortby,
            order,
            direction,
          },
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async fetchFeedbackTrend({
    query,
    payload,
  }: {
    query: {
      view_type: string;
    };
    payload: StructuredFilter;
  }): Promise<FeedbackListTrendDataT> {
    try {
      const queryString = buildObjQuery(query);
      const wa = getCurrWorkspaceAlias();
      const sanitizedPayload = sanitizeStructuredFilterPayload(payload);
      const res = await defaultAxios.post(
        `/workspaces/${wa}/feedbacks/trend${queryString}`,
        sanitizedPayload,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async fetchFeedbackDetail(
    feedbackAlias: string,
    isPublic?: boolean,
  ): Promise<FeedbackT> {
    try {
      const url = isPublic
        ? `/feedbacks/${feedbackAlias}/public`
        : `/feedbacks/${feedbackAlias}`;
      const res = await defaultAxios.get<FeedbackT>(url);
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async markAsArchive(feedbackAlias: string) {
    try {
      const res = await defaultAxios.put(`/feedbacks/${feedbackAlias}/archive`);
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async markAsUnarchive(feedbackAlias: string) {
    try {
      const res = await defaultAxios.put(
        `/feedbacks/${feedbackAlias}/unarchive`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async postFeedback(
    feedback: PostFeedbackParams,
    isCSV?: boolean,
  ): Promise<FeedbackT> {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.post(
        `/workspaces/${wa}/feedback?csv=${isCSV ? 1 : 0}`,
        feedback,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async uploadFeedback(feedbacks: JSONFeedbackUploadParams[]) {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.post(
        `/workspaces/${wa}/feedback/upload`,
        feedbacks,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  // Deprecated: Use removeFeedbacks instead
  async removeFeedback(alias: string) {
    try {
      const res = await defaultAxios.delete(`/feedbacks/${alias}`);
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async removeFeedbacks(feedbackAliases: string[]) {
    if (feedbackAliases.length === 0) return;

    try {
      const wa = getCurrWorkspaceAlias();
      const query = buildObjQuery({ feedback_id: feedbackAliases.join('|') });
      const res = await defaultAxios.delete(
        `/workspaces/${wa}/feedbacks${query}`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async addKeyword(feedbackKey: string, keywordKey: string) {
    try {
      const res = await defaultAxios.post(`/feedbacks/${feedbackKey}/keyword`, {
        alias: keywordKey,
      });
      return res.data;
    } catch (e) {
      console.error(e);
    }
  },
  async removeKeyword(feedbackKey: string, keywordKey: string) {
    try {
      const res = await defaultAxios.patch(
        `/feedbacks/${feedbackKey}/keyword`,
        {
          alias: keywordKey,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
    }
  },
  async addTag(feedbackKey: string, tagKey: string) {
    try {
      const res = await defaultAxios.post(`/feedbacks/${feedbackKey}/label`, {
        alias: tagKey,
      });
      return res.data;
    } catch (e) {
      console.error(e);
    }
  },
  async removeTag(feedbackKey: string, tagKey: string) {
    try {
      const res = await defaultAxios.patch(`/feedbacks/${feedbackKey}/label`, {
        alias: tagKey,
      });
      return res.data;
    } catch (e) {
      console.error(e);
    }
  },
  async changeTitle(feedbackKey: string, title: string) {
    try {
      const res = await defaultAxios.put(`/feedbacks/${feedbackKey}/title`, {
        title,
      });
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async changeSentiment(feedbackKey: string, sentimentKey: number) {
    try {
      const res = await defaultAxios.put(
        `/feedbacks/${feedbackKey}/sentiment`,
        {
          sentiment: sentimentKey,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async changeEndingSentiment(feedbackKey: string, sentimentKey: number) {
    try {
      const res = await defaultAxios.put(
        `/feedbacks/${feedbackKey}/ending_sentiment`,
        {
          sentiment: sentimentKey,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async fetchRelateFeedbacks(feedbackKey: string): Promise<string[]> {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.get(
        `/workspaces/${wa}/feedbacks/relate/${feedbackKey}`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async fetchKeywordGroupedFeedbacks(): Promise<KeywordGroupedFeedback[]> {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.get(
        `/workspaces/${wa}/feedbacks/keywords_group`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async changePriority(feedbackAlias: string, priorityKey: number) {
    try {
      const res = await defaultAxios.put(
        `/feedbacks/${feedbackAlias}/priority`,
        {
          priority: Number(priorityKey),
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async resetAssignee(feedbackAlias: string) {
    try {
      const res = await defaultAxios.put(
        `/feedbacks/${feedbackAlias}/assignee/reset`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
    }
  },
  async changeAssignee(feedbackAlias: string, email: string) {
    try {
      const res = await defaultAxios.put(
        `/feedbacks/${feedbackAlias}/assignee`,
        {
          assignee: email,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async changeDataOrigin(feedbackAlias: string, data_origin: string) {
    try {
      const res = await defaultAxios.post(
        `/feedbacks/${feedbackAlias}/data_origin`,
        {
          data_origin,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async searchRemarks(remark: string): Promise<{
    remarks: string[];
  }> {
    try {
      const wa = getCurrWorkspaceAlias();
      const query = buildObjQuery({
        remark,
      });
      const res = await defaultAxios.get(`/workspaces/${wa}/remarks${query}`);
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  async addRemarks(feedbackAlias: string, remark: string) {
    try {
      const res = await defaultAxios.patch(
        `/feedbacks/${feedbackAlias}/remarks/push`,
        {
          remark,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async removeRemark(feedbackAlias: string, remark: string) {
    try {
      const res = await defaultAxios.patch(
        `/feedbacks/${feedbackAlias}/remarks/pull`,
        {
          remark,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async resetDataOrigin(feedbackAlias: string) {
    try {
      const res = await defaultAxios.put(
        `/feedbacks/${feedbackAlias}/data_origin/reset`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async changeAgent(feedbackAlias: string, agentAlias: string) {
    try {
      const res = await defaultAxios.patch(
        `/feedbacks/${feedbackAlias}/manager`,
        {
          alias: agentAlias,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async resetAgent(feedbackAlias: string) {
    try {
      const res = await defaultAxios.put(
        `/feedbacks/${feedbackAlias}/manager/reset`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async updateDate(feedbackAlias: string, date: string): Promise<FeedbackT> {
    try {
      const res = await defaultAxios.put(`/feedbacks/${feedbackAlias}/date`, {
        date,
      });
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async updateScore(
    feedbackAlias: string,
    score: number | null,
    field_name: string,
  ): Promise<FeedbackT> {
    try {
      const res = await defaultAxios.put(
        `/feedbacks/${feedbackAlias}/${field_name}`,
        {
          score,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  postBookmarkToFeedback: async (
    data: PostBookmarkParams,
    feedbackAlias: string,
  ): Promise<BookmarkT> => {
    try {
      const res = await defaultAxios.post(
        `/feedbacks/${feedbackAlias}/bookmark`,
        data,
      );

      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  deleteBookmarkToFeedback: async (
    bookmarkAlias: string,
    feedbackAlias: string,
  ) => {
    try {
      await defaultAxios.patch(
        `/feedbacks/${feedbackAlias}/bookmarks/${bookmarkAlias}`,
      );
      return true;
    } catch (e) {
      return false;
    }
  },
  editBookmarkToFeedback: async (
    data: PostBookmarkParams,
    bookmarkAlias: string,
    feedbackAlias: string,
  ) => {
    try {
      await defaultAxios.put(
        `/feedbacks/${feedbackAlias}/bookmarks/${bookmarkAlias}`,
        data,
      );
      return true;
    } catch (e) {
      return false;
    }
  },
  async changeClassification(
    feedbackAlias: string,
    classification_id: CATEGORY_TYPE[],
  ) {
    try {
      const res = await defaultAxios.patch(
        `/feedbacks/${feedbackAlias}/classification`,
        {
          classification_id,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async resetClassification(feedbackAlias: string) {
    try {
      const res = await defaultAxios.put(
        `/feedbacks/${feedbackAlias}/classification/reset`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async fetchFeedbackActivities(feedbackAlias: string) {
    try {
      const res = await defaultAxios.get(
        `/feedbacks/${feedbackAlias}/activities`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async postComment(feedbackAlias: string, comment: string) {
    try {
      const res = await defaultAxios.post(
        `/feedbacks/${feedbackAlias}/comment`,
        {
          comment,
        },
      );
      return res;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async updateComment(
    feedbackAlias: string,
    commentAlias: string,
    comment: string,
  ) {
    try {
      const res = await defaultAxios.patch(
        `/feedbacks/${feedbackAlias}/comment/${commentAlias}`,
        {
          comment,
        },
      );
      return res;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async deleteComment(feedbackAlias: string, commentAlias: string) {
    try {
      const res = await defaultAxios.delete(
        `/feedbacks/${feedbackAlias}/activity/${commentAlias}`,
      );
      return res;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async exportFeedbackList(params: {
    reason?: string;
    password?: string;
    payload: StructuredFilter;
  }) {
    try {
      const wa = getCurrWorkspaceAlias();
      const sanitizedPayload = sanitizeStructuredFilterPayload(params.payload);
      const res = await defaultAxios.post(
        `/workspaces/${wa}/feedbacks/export`,
        {
          reason: params.reason,
          password: params.password,
          filter: sanitizedPayload,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async updateMultipleFeedbackField<T extends keyof FeedbackT>(
    feedback_aliases: string[],
    field: T,
    value: FeedbackT[T] | null,
  ) {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.patch(
        `workspaces/${wa}/feedbacks/${field}`,
        {
          feedbacks: feedback_aliases,
          value: value || undefined,
        },
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async searchSourceTarget(source_target: string): Promise<{
    source_targets: string[];
  }> {
    try {
      const wa = getCurrWorkspaceAlias();
      const query = buildObjQuery({
        [SOURCE_COLUMNS.SOURCE_TARGET]: source_target,
      });
      const res = await defaultAxios.get(
        `/workspaces/${wa}/source_targets${query}`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  async fetchFeedbackBlockListByReferences(
    references: {
      feedback_id: string;
      block_id: string[];
    }[],
  ): Promise<FeedbackListItemWithBlock[]> {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.post(
      `/workspaces/${wa}/feedbacks/multiple_blocks`,
      references,
    );
    return res.data;
  },
};

export default feedbackService;
